import { normalizarRut } from "./rut";
import { client } from "./supabase";

// Nombre a MOSTRAR de un proveedor (regla 10.1 — fallback centralizado). Devuelve el `alias`
// (nombre de fantasía, ej. "SendGrid" para "Twilio SendGrid") si está poblado, o el `nombre`
// formal (razón social) si no. El `.trim()` hace que un alias "" (cadena vacía, no null) caiga
// al nombre formal. SOLO para display — el matching/comparación es siempre por ID (regla 10.7),
// nunca por este valor. El `nombre` formal y los snapshots persistidos no se tocan.
export const getNombreProveedor = (prov) => prov?.alias?.trim() || prov?.nombre || "";

// Concepto compuesto de una línea de flujo de proveedor recurrente (regla 10.1 — armado
// centralizado, ÚNICA fuente del string). La proyección (proveedoresRecurrentes.js) y la
// conciliación contra el bancario (conciliacionCostoRecurrente.js) deben producir EXACTAMENTE el
// mismo texto para seguir macheando, por eso ambas pasan por acá y no por copias inline. Devuelve
// "{nombrePrincipal} | {campoSecundario}"; sin campo secundario (falsy/vacío) → solo el nombre
// principal. Separador " | " (pipe). SOLO display (regla 10.7): el matching es siempre por ID.
export const conceptoFlujoProveedor = (nombrePrincipal, campoSecundario) => {
  const secundario = campoSecundario?.trim();
  return secundario ? `${nombrePrincipal} | ${secundario}` : nombrePrincipal;
};

// Un proveedor por ID (regla 10.7 — lookup por id, nunca por nombre). Devuelve null si no existe.
export const getProveedorPorId = async (id) => {
  if (!id) return null;
  const { data, error } = await client.from("proveedores").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data || null;
};

export const getProveedores = async (pais, soloHabilitados = true) => {
  let query = client.from("proveedores").select("*").eq("pais", pais);
  if (soloHabilitados) query = query.eq("habilitado", true);
  query = query.order("nombre", { ascending: true });
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Crea un proveedor con guard anti-duplicado por RUT (regla 10.7). Como los `rut_ruc`
// guardados están en formatos heterogéneos (con/sin puntos, guión Unicode −, sin DV), la
// comparación se hace NORMALIZADA en memoria, no con un `.eq` a la columna cruda. Scope por país
// (el RUT es una clave nacional). Extranjeros sin `rut_ruc` se insertan sin guard.
//
// Retorno:
//   - éxito → la fila creada (tiene `.id`).
//   - duplicado → `{ yaExiste: true, proveedor: { id, nombre, rut_ruc } }` (NO inserta).
export const crearProveedor = async (proveedorData) => {
  const rutNuevo = normalizarRut(proveedorData?.rut_ruc);
  if (rutNuevo) {
    let query = client.from("proveedores").select("id, nombre, rut_ruc").not("rut_ruc", "is", null);
    if (proveedorData?.pais) query = query.eq("pais", proveedorData.pais);
    const { data: existentes, error: errBusca } = await query;
    if (errBusca) throw errBusca;
    const dup = (existentes || []).find((p) => normalizarRut(p.rut_ruc) === rutNuevo);
    if (dup) return { yaExiste: true, proveedor: dup };
  }
  const { data, error } = await client.from("proveedores").insert([proveedorData]).select();
  if (error) throw error;
  return data?.[0];
};

export const actualizarProveedor = async (id, proveedorData) => {
  const { data, error } = await client
    .from("proveedores")
    .update({ ...proveedorData, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

// ── Costos asociados de un contrato recurrente (tabla proveedor_costos_asociados) ──
// Lookup por proveedor_id (regla 10.7). Devuelve [] si no hay.
export const getCostosAsociados = async (proveedorId) => {
  if (!proveedorId) return [];
  const { data, error } = await client
    .from("proveedor_costos_asociados")
    .select("*")
    .eq("proveedor_id", proveedorId)
    .order("id", { ascending: true });
  if (error) throw error;
  return data || [];
};

// Persiste la lista de costos de un proveedor con estrategia REPLACE (borra los existentes e
// inserta la lista actual). Filtra filas vacías (sin concepto y sin monto). NO atómico (patrón
// del proyecto: sin transacción multi-tabla) — el borrado y el insert son secuenciales.
export const guardarCostosAsociados = async (proveedorId, costos) => {
  if (!proveedorId) return;
  const { error: errDel } = await client.from("proveedor_costos_asociados").delete().eq("proveedor_id", proveedorId);
  if (errDel) throw errDel;

  const validos = (costos || [])
    .filter((c) => (c.concepto && c.concepto.trim()) || Number(c.monto) > 0)
    .map((c) => {
      const esUnico = c.tipo === "unico";
      return {
        proveedor_id: proveedorId,
        concepto: c.concepto?.trim() || null,
        monto: c.monto === "" || c.monto == null ? null : Number(c.monto),
        moneda: c.moneda || "CLP",
        tipo: esUnico ? "unico" : "mensual",
        // Coherencia: mensual usa dia_pago (fecha null); único usa fecha (dia_pago null).
        dia_pago: esUnico || c.dia_pago === "" || c.dia_pago == null ? null : Number(c.dia_pago),
        fecha: esUnico ? c.fecha || null : null,
      };
    });
  if (validos.length > 0) {
    const { error: errIns } = await client.from("proveedor_costos_asociados").insert(validos);
    if (errIns) throw errIns;
  }
};
